import type { MeetingRecording } from '@/lib/api/meeting-recordings.api'
import { useCallback, useEffect, useRef } from 'react'
import { meetingRecordingsApi } from '@/lib/api/meeting-recordings.api'
import { useRealtime } from '@/lib/realtime/core/useRealtime'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { SocketEvent } from '@scrambl/shared'

export interface MeetingRecordingUpdatePayload {
  recordingId: string
  status: string
  title?: string | null
  noteId?: string | null
  errorStage?: string | null
  errorMessage?: string | null
}

export const MEETING_RECORDINGS_KEY = ['meeting-recordings'] as const

// Statuses where the pipeline is still working — poll while any row is here.
const TRANSIENT_STATUSES = new Set([
  'UPLOADING',
  'UPLOADED',
  'TRANSCRIBING',
  'SUMMARIZING',
])

export function useMeetingRecordings(params?: {
  page?: number
  limit?: number
  search?: string
  enabled?: boolean
}) {
  const queryClient = useQueryClient()
  const { socket, isReady } = useRealtime({ suppressLogging: true })
  const search = params?.search?.trim() || undefined

  const query = useQuery({
    queryKey: [
      ...MEETING_RECORDINGS_KEY,
      params?.page ?? 1,
      params?.limit ?? 50,
      search ?? '',
    ],
    queryFn: () =>
      meetingRecordingsApi.listRecordings({
        page: params?.page,
        limit: params?.limit,
        search,
      }),
    placeholderData: previous => previous,
    enabled: params?.enabled !== false,
    staleTime: 5_000,
    // Polling fallback: realtime socket events are the fast path, but if one
    // is missed (reconnect, dropped room, etc.) this guarantees the list
    // still converges. Only polls while something is actively processing, so
    // an idle Meetings tab makes no extra requests.
    refetchInterval: q => {
      const data = q.state.data as
        | { recordings: MeetingRecording[] }
        | undefined
      const hasActive = data?.recordings.some(r =>
        TRANSIENT_STATUSES.has(r.status),
      )
      return hasActive ? 5_000 : false
    },
    refetchIntervalInBackground: false,
  })

  // Patch the cache from pipeline status events, fall back to invalidation
  const handleUpdate = useCallback(
    (payload: MeetingRecordingUpdatePayload) => {
      let patched = false
      queryClient.setQueriesData(
        { queryKey: MEETING_RECORDINGS_KEY },
        (cached: unknown) => {
          if (
            !cached ||
            typeof cached !== 'object' ||
            !('recordings' in cached)
          ) {
            return cached
          }
          const data = cached as {
            recordings: MeetingRecording[]
            total: number
          }
          const exists = data.recordings.some(
            recording => recording.id === payload.recordingId,
          )
          if (!exists) return cached
          patched = true
          return {
            ...data,
            recordings: data.recordings.map(recording =>
              recording.id === payload.recordingId
                ? {
                    ...recording,
                    status: payload.status,
                    title: payload.title ?? recording.title,
                    noteId: payload.noteId ?? recording.noteId,
                    errorStage: payload.errorStage ?? null,
                    errorMessage: payload.errorMessage ?? null,
                  }
                : recording,
            ),
          }
        },
      )
      if (!patched) {
        void queryClient.invalidateQueries({ queryKey: MEETING_RECORDINGS_KEY })
      }
      // A READY recording created a note — refresh note lists too
      if (payload.status === 'READY') {
        void queryClient.invalidateQueries({ queryKey: ['notes'] })
      }
    },
    [queryClient],
  )

  const handlerRef = useRef(handleUpdate)
  useEffect(() => {
    handlerRef.current = handleUpdate
  }, [handleUpdate])

  useEffect(() => {
    if (!socket || !isReady) return
    const listener = (payload: MeetingRecordingUpdatePayload) =>
      handlerRef.current(payload)
    socket.on(SocketEvent.MEETING_RECORDING_UPDATED, listener)
    return () => {
      socket.off(SocketEvent.MEETING_RECORDING_UPDATED, listener)
    }
  }, [socket, isReady])

  const retryMutation = useMutation({
    mutationFn: (id: string) => meetingRecordingsApi.retry(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MEETING_RECORDINGS_KEY })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => meetingRecordingsApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MEETING_RECORDINGS_KEY })
    },
  })

  return {
    recordings: query.data?.recordings ?? [],
    total: query.data?.total ?? 0,
    // Pending counts as loading: a query waiting on the persisted cache is not
    // fetching yet, and isLoading would show the empty recordings state.
    isLoading:
      query.isLoading || (params?.enabled !== false && query.isPending),
    isError: query.isError,
    refetch: query.refetch,
    retryRecording: retryMutation.mutateAsync,
    isRetrying: retryMutation.isPending,
    deleteRecording: deleteMutation.mutateAsync,
  }
}
